/* eslint-disable @typescript-eslint/no-explicit-any */
import {
    MarketplaceAddress,
    MarketplaceAttributes,
    MarketplaceCalendar,
    MarketplaceCustomQuestions,
    MarketplaceSettings,
    MarketplaceSpecialHours,
    MarketplaceTaxes,
    MarketplaceTipRates,
    Organization,
    SaveOrganization
} from 'model/organization';
import {
    OrganizationDto,
    SaveOrganizationDto,
    OrganizationMarketplaceAttributesDto,
    OrganizationAddressDto,
    OrganizationCalendarDto,
    OrganizationSpecialHoursDto,
    OrganizationTaxesDto,
    OrganizationTipRatesDto,
    OrganizationSettingsDto,
    OrganizationCustomQuestionsDto
} from './OrganizationDtos';

/**
 * Convert Organization DTO to Organization Model
 */
export const convertToOrganization = (dto: OrganizationDto): Organization => {
    return {
        id: dto.id,
        parentOrganizationId: dto.parent_organization_id,
        uuid: dto.uuid,
        logo: dto.logo,
        name: dto.name,
        businessType: dto.business_type,
        nicknameDba: dto.nickname_dba,
        taxId: dto.tax_id,
        email: dto.email,
        phone: dto.phone,
        website: dto.website,
        addressLineOne: dto.address_line_one,
        addressLineTwo: dto.address_line_two,
        city: dto.city,
        state: dto.state,
        zipcode: dto.zipcode,
        country: dto.country,
        latitude: dto.latitude,
        active: dto.active,
        createdAt: new Date(dto.created_at),
        updatedAt: dto.updated_at ? new Date(dto.updated_at) : undefined,
        marketplacesCount: dto.marketplaces_count,
        marketplaceIds: dto.marketplace_ids
    };
};

/**
 * Convert MarketplaceCalendar to OrganizationCalendarDto
 */
const convertCalendarToDto = (cal: MarketplaceCalendar): OrganizationCalendarDto => {
    return {
        id: cal.id,
        marketplace_id: cal.marketplaceId,
        owner: cal.owner,
        name: cal.name,
        range: cal.range,
        slots: cal.slots,
        specific_date_slots: cal.specificDateSlots,
        time_zone: cal.timeZone,
        active: cal.active
    };
};

/**
 * Convert MarketplaceSpecialHours to OrganizationSpecialHoursDto
 */
const convertSpecialHoursToDto = (sh: MarketplaceSpecialHours): OrganizationSpecialHoursDto => {
    return {
        id: sh.id,
        marketplace_id: sh.marketplaceId,
        name: sh.name,
        business_hours: sh.businessHours,
        date: sh.date,
        closed: sh.closed,
        _destroy: sh.destroy
    };
};

/**
 * Convert MarketplaceTaxes to OrganizationTaxesDto
 */
const convertTaxesToDto = (tax: MarketplaceTaxes): OrganizationTaxesDto => {
    return {
        id: tax.id,
        uuid: tax.uuid,
        marketplace_id: tax.marketplaceId,
        name: tax.name,
        value: tax.value,
        _destroy: tax.destroy
    };
};

/**
 * Convert MarketplaceTipRates to OrganizationTipRatesDto
 */
const convertTipRatesToDto = (tip: MarketplaceTipRates): OrganizationTipRatesDto => {
    return {
        id: tip.id,
        marketplace_id: tip.marketplaceId,
        name: tip.name,
        value: tip.value
    };
};

/**
 * Convert MarketplaceSettings to OrganizationSettingsDto
 */
const convertSettingsToDto = (setting: MarketplaceSettings): OrganizationSettingsDto => {
    return {
        id: setting.id,
        owner_id: setting.ownerId,
        owner_type: setting.ownerType,
        uuid: setting.uuid,
        name: setting.name,
        description: setting.description,
        key: setting.key,
        value: setting.value,
        active: setting.active
    };
};

/**
 * Convert MarketplaceCustomQuestions to OrganizationCustomQuestionsDto
 */
const convertCustomQuestionsToDto = (q: MarketplaceCustomQuestions): OrganizationCustomQuestionsDto => {
    return {
        id: q.id,
        question_text: q.questionText,
        category: q.category,
        display_order: q.displayOrder,
        enabled: q.enabled,
        required: q.required,
        _destroy: q.destroy
    };
};

/**
 * Convert MarketplaceAddress to OrganizationAddressDto
 */
const convertAddressToDto = (addr: MarketplaceAddress): OrganizationAddressDto => {
    return {
        id: addr.id,
        marketplace_id: addr.marketplaceId,
        owner: addr.owner,
        name: addr.name,
        address_line_one: addr.addressLineOne,
        address_line_two: addr.addressLineTwo,
        city: addr.city,
        state: addr.state,
        zipcode: addr.zipcode,
        country: addr.country,
        main: addr.main,
        latitude: addr.latitude,
        longitude: addr.longitude,
        active: addr.active,
        amenity_list: addr.amenityList,
        staff_training_list: addr.staffTrainingList
    };
};

/**
 * Convert MarketplaceAttributes to OrganizationMarketplaceAttributesDto
 */
const convertMarketplaceAttributesToDto = (attributes: MarketplaceAttributes): OrganizationMarketplaceAttributesDto => {
    return {
        user_id: attributes.userId,
        business_name: attributes.businessName,
        email: attributes.email,
        phone: attributes.phone,
        zipcode: attributes.zipcode,
        years_in_business: attributes.yearsInBusiness,
        employees: attributes.employees,
        locations_count: attributes.locationsCount,
        website: attributes.website,
        logo_main: attributes.logoMain,
        logo_secondary: attributes.logoSecondary,
        booknow_image: attributes.booknowImage,
        tag_line: attributes.tagLine,
        tag_line_secondary: attributes.tagLineSecondary,
        ranking_script: attributes.rankingScript,
        dogs_detail: attributes.dogsDetail,
        cats_detail: attributes.catsDetail,
        exotics_detail: attributes.exoticsDetail,
        email_content: attributes.emailContent,
        ga_tracking_id: attributes.gaTrackingId,
        mobile_business: attributes.mobileBusiness,
        local_business: attributes.localBusiness,
        athome_business: attributes.athomeBusiness,
        inhome_business: attributes.inhomeBusiness,
        optin_email_new_bookings: attributes.optinEmailNewBookings,
        optin_email_cancellations: attributes.optinEmailCancellations,
        online_booking: attributes.onlineBooking,
        onboarding_step: attributes.onboardingStep,
        tax_rate: attributes.taxRate,
        hear_about_us: attributes.hearAboutUs,
        deposit_category: attributes.depositCategory,
        deposit_value: attributes.depositValue,
        url_slug: attributes.urlSlug,
        twilio_phone_number: attributes.twilioPhoneNumber,
        active: attributes.active,
        payment_enabled: attributes.paymentEnabled,
        promo_code: attributes.promoCode,
        sms_marketing_optin: attributes.smsMarketingOptin,
        country: attributes.country,
        olb_minimum_lead_time: attributes.olbMinimumLeadTime,
        olb_maximum_lead_time: attributes.olbMaximumLeadTime,
        olb_enabled: attributes.olbEnabled,
        olb_can_select_staff: attributes.olbCanSelectStaff,
        olb_accept_new_client: attributes.olbAcceptNewClient,
        intake_form: attributes.intakeForm,
        custom_questions_enabled: attributes.customQuestionsEnabled,
        olb_booking_request: attributes.olbBookingRequest,
        pricing_option: attributes.pricingOption,
        schedule_type: attributes.scheduleType,
        business_area_list: attributes.businessAreaList,
        pet_list: attributes.petList,
        pet_age_list: attributes.petAgeList,
        pet_size_list: attributes.petSizeList,
        pet_requirement_list: attributes.petRequirementList,
        pet_vaccine_list: attributes.petVaccineList,
        pet_hair_list: attributes.petHairList,
        payment_type_list: attributes.paymentTypeList,
        custom_pet_positive_personality_list: attributes.customPetPositivePersonalityList,
        custom_pet_negative_personality_list: attributes.customPetNegativePersonalityList,
        calendars_attributes: attributes.calendarsAttributes?.map(convertCalendarToDto),
        special_hours_attributes: attributes.specialHoursAttributes?.map(convertSpecialHoursToDto),
        taxes_attributes: attributes.taxesAttributes?.map(convertTaxesToDto),
        tip_rates_attributes: attributes.tipRatesAttributes?.map(convertTipRatesToDto),
        settings_attributes: attributes.settingsAttributes?.map(convertSettingsToDto),
        custom_questions_attributes: attributes.customQuestionsAttributes?.map(convertCustomQuestionsToDto),
        addresses_attributes: attributes.addressesAttributes?.map(convertAddressToDto)
    };
};

/**
 * Convert SaveOrganization Model to SaveOrganizationDto
 */
export const convertToSaveOrganizationDto = (organization: SaveOrganization): SaveOrganizationDto => {
    return {
        parent_organization_id: organization.parentOrganizationId ?? undefined,
        logo: organization.logo ?? undefined,
        name: organization.name,
        business_type: organization.businessType ?? undefined,
        nickname_dba: organization.nicknameDba ?? undefined,
        tax_id: organization.taxId,
        email: organization.email,
        phone: organization.phone ?? undefined,
        website: organization.website ?? undefined,
        address_line_one: organization.addressLineOne,
        address_line_two: organization.addressLineTwo ?? undefined,
        city: organization.city,
        state: organization.state,
        zipcode: organization.zipcode,
        country: organization.country ?? undefined,
        active: organization.active ?? true,
        marketplace_ids: organization.marketplaceIds ?? undefined,
        marketplaces_attributes:
            organization.marketplacesAttributes?.map(convertMarketplaceAttributesToDto) ?? undefined
    };
};
